import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './HomeCarousel.css';

const HomeCarousel = ({ items, defaultInterval = 5 }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPageVisible, setIsPageVisible] = useState(
    typeof document !== 'undefined' ? document.visibilityState !== 'hidden' : true
  );
  const navigate = useNavigate();
  const slides = items || [];

  const mediaStateRef = React.useRef({});

  const ensureMediaState = useCallback((slide) => {
    if (!slide || !Array.isArray(slide.media_list) || slide.media_list.length === 0) return;
    const id = slide.id;
    if (!id) return;
    const list = slide.media_list;
    const len = list.length;
    if (len === 0) return;
    const mode = slide.media_play_mode || 'sequence';
    const state = mediaStateRef.current[id] || {};
    if (!state.sequence || state.sequence.length !== len) {
      // 初始化 sequence
      let sequence = Array.from({ length: len }, (_, i) => i);
      if (mode === 'shuffle') {
        for (let i = len - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [sequence[i], sequence[j]] = [sequence[j], sequence[i]];
        }
      }
      mediaStateRef.current[id] = {
        mode,
        sequence,
        cursor: 0,
        currentIndex: sequence[0],
      };
    }
  }, []);

  const advanceMediaForSlide = useCallback((slide) => {
    if (!slide || !Array.isArray(slide.media_list) || slide.media_list.length === 0) return;
    const id = slide.id;
    if (!id) return;
    const list = slide.media_list;
    const len = list.length;
    if (len === 0) return;
    const mode = slide.media_play_mode || 'sequence';
    let state = mediaStateRef.current[id];
    if (!state || !state.sequence || state.sequence.length !== len || state.mode !== mode) {
      // 重新初始化
      let sequence = Array.from({ length: len }, (_, i) => i);
      if (mode === 'shuffle') {
        for (let i = len - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [sequence[i], sequence[j]] = [sequence[j], sequence[i]];
        }
      }
      state = {
        mode,
        sequence,
        cursor: 0,
        currentIndex: sequence[0],
      };
      mediaStateRef.current[id] = state;
      return;
    }
    // 使用当前 cursor 显示下一个媒体
    const { sequence, cursor } = state;
    const idx = sequence[cursor];
    const nextCursor = cursor + 1;
    state.currentIndex = idx;
    if (nextCursor >= len) {
      if (mode === 'shuffle') {
        // 重新洗牌
        let newSeq = Array.from({ length: len }, (_, i) => i);
        for (let i = len - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [newSeq[i], newSeq[j]] = [newSeq[j], newSeq[i]];
        }
        state.sequence = newSeq;
        state.cursor = 0;
      } else {
        state.cursor = 0;
      }
    } else {
      state.cursor = nextCursor;
    }
  }, []);

  const goToSlide = useCallback((index) => {
    if (slides.length === 0) return;
    const normalized = (index + slides.length) % slides.length;
    const targetSlide = slides[normalized];
    if (targetSlide && Array.isArray(targetSlide.media_list) && targetSlide.media_list.length > 0) {
      advanceMediaForSlide(targetSlide);
    }
    setActiveIndex(normalized);
  }, [slides, advanceMediaForSlide]);

  // 监听标签页可见性变化，隐藏时暂停视频与轮播
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState !== 'hidden';
      setIsPageVisible(visible);
      if (!visible) {
        // 暂停当前页面所有轮播视频，避免继续拉取分片
        const videos = document.querySelectorAll('.home-carousel video');
        videos.forEach((v) => {
          try {
            v.pause();
          } catch (e) {
            // ignore
          }
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 自动切换 + 初始化当前轮播的媒体状态
  useEffect(() => {
    if (slides.length === 0) return;
    const slide = slides[activeIndex];
    if (slide && Array.isArray(slide.media_list) && slide.media_list.length > 0) {
      ensureMediaState(slide);
      advanceMediaForSlide(slide);
    }
    if (!isPageVisible) return;
    if (slides.length <= 1) return;
    const interval = (slide && slide.switch_interval) ? slide.switch_interval * 1000 : defaultInterval * 1000;
    const timer = setInterval(() => {
      goToSlide(activeIndex + 1);
    }, interval);
    return () => clearInterval(timer);
  }, [activeIndex, slides, defaultInterval, goToSlide, ensureMediaState, advanceMediaForSlide, isPageVisible]);

  if (slides.length === 0) return null;

  const current = slides[activeIndex];
  const getFit = (slide) => (slide && slide.media_fit) || 'cover';
  const getAlignPercent = (slide) => {
    if (!slide) return 50;
    const align = slide.media_align || 'center';
    if (align === 'top') return 0;
    if (align === 'bottom') return 100;
    if (align === 'custom') {
      const p = Number(slide.media_align_percent);
      if (Number.isFinite(p)) {
        return Math.min(100, Math.max(0, p));
      }
      return 50;
    }
    return 50;
  };

  const isTextBgEnabled = (slide) => slide && (slide.text_bg_enabled === 1 || slide.text_bg_enabled === true);

  const buildTextBoxStyle = (slide) => {
    if (!isTextBgEnabled(slide)) return {};
    const rawColor = (slide.text_bg_color || '#000000').trim();
    const color = /^#([0-9a-fA-F]{6})$/.test(rawColor) ? rawColor : '#000000';
    const opacityRaw = Number(slide.text_bg_opacity);
    const opacity = Number.isFinite(opacityRaw) ? Math.min(100, Math.max(0, opacityRaw)) : 40;
    const alpha = opacity / 100;

    let r = 0; let g = 0; let b = 0;
    try {
      r = parseInt(color.slice(1, 3), 16);
      g = parseInt(color.slice(3, 5), 16);
      b = parseInt(color.slice(5, 7), 16);
    } catch {
      r = 0; g = 0; b = 0;
    }

    const maxWidthPercentRaw = Number(slide.text_bg_max_width_percent);
    const maxWidthPercent = Number.isFinite(maxWidthPercentRaw)
      ? Math.min(100, Math.max(10, maxWidthPercentRaw))
      : 60;

    const paddingVRaw = Number(slide.text_bg_padding_v);
    const paddingHRaw = Number(slide.text_bg_padding_h);
    const radiusRaw = Number(slide.text_bg_radius);

    return {
      maxWidth: `${maxWidthPercent}%`,
      padding: `${Number.isFinite(paddingVRaw) ? paddingVRaw : 16}px ${Number.isFinite(paddingHRaw) ? paddingHRaw : 24}px`,
      borderRadius: `${Number.isFinite(radiusRaw) ? radiusRaw : 16}px`,
      backgroundColor: `rgba(${r}, ${g}, ${b}, ${alpha})`,
    };
  };

  const handleClick = () => {
    const url = current?.link_url;
    if (!url || !url.trim()) return;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  const hasLink = current?.link_url && current.link_url.trim();

  return (
    <div className={`home-carousel ${hasLink ? 'clickable' : ''}`} onClick={handleClick}>
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`carousel-slide ${index === activeIndex ? 'active' : ''}`}
          style={{ zIndex: index === activeIndex ? 2 : 1 }}
        >
          {(() => {
            const mediaList = slide.media_list;
            let media = null;
            if (Array.isArray(mediaList) && mediaList.length > 0 && slide.id && mediaStateRef.current[slide.id]) {
              const state = mediaStateRef.current[slide.id];
              const idx = state.currentIndex ?? 0;
              media = mediaList[Math.min(mediaList.length - 1, Math.max(0, idx))];
            }
            const bgType = media ? media.media_type : slide.background_type;
            const bgUrl = media ? media.url : slide.background_url;
            const fitSource = media || slide;
            if (bgType === 'video' && bgUrl) {
              return (
                <video
                  className={`carousel-bg-media fit-${getFit(fitSource)}`}
                  style={{ objectPosition: `50% ${getAlignPercent(fitSource)}%` }}
                  src={bgUrl}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              );
            }
            if (bgUrl && String(bgUrl).trim()) {
              return (
                <div
                  className={`carousel-bg-media carousel-bg-image fit-${getFit(fitSource)}`}
                  style={{
                    backgroundImage: `url("${String(bgUrl).trim().replace(/"/g, '%22')}")`,
                    backgroundPosition: `50% ${getAlignPercent(fitSource)}%`
                  }}
                />
              );
            }
            return <div className="carousel-bg-gradient" />;
          })()}
          <div className="carousel-overlay" />
          <div className="carousel-content">
            {isTextBgEnabled(slide) ? (
              <div className="carousel-text-box" style={buildTextBoxStyle(slide)}>
                <h2 className="carousel-title">{slide.title}</h2>
                {slide.subtitle && <p className="carousel-subtitle">{slide.subtitle}</p>}
              </div>
            ) : (
              <>
                <h2 className="carousel-title">{slide.title}</h2>
                {slide.subtitle && <p className="carousel-subtitle">{slide.subtitle}</p>}
              </>
            )}
          </div>
        </div>
      ))}
      {slides.length > 1 && (
        <div className="carousel-dots" onClick={(e) => e.stopPropagation()}>
          {slides.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === activeIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`切换到第 ${index + 1} 张`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HomeCarousel;
